import React, { useState, useEffect } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { Mail, Phone, Building2, Calendar, Filter, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, neu, bearbeitet, abgeschlossen

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' 
        ? `${API}/contact-inquiries`
        : `${API}/contact-inquiries?status=${filter}`;
      
      const response = await axios.get(url);
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [filter]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'neu': return 'bg-red-100 text-red-800';
      case 'bearbeitet': return 'bg-yellow-100 text-yellow-800';
      case 'abgeschlossen': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getGewerkName = (gewerk) => {
    const gewerkMap = {
      'hochbau': 'Hochbau',
      'ausbau': 'Ausbau',
      'galabau': 'Gala-Bau',
      'tiefbau': 'Tiefbau',
      'bauelemente': 'Bauelemente',
      'fliesen': 'Fliesen',
      'sonstiges': 'Sonstiges'
    };
    return gewerkMap[gewerk] || gewerk;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Kontaktanfragen Verwaltung
            </h1>
            <p className="text-lg text-gray-600">
              Übersicht aller eingegangenen Anfragen
            </p>
          </div>

          {/* Stats & Filters */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">{inquiries.length}</div>
                  <div className="text-sm text-gray-600 mt-1">Gesamt</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600">
                    {inquiries.filter(i => i.status === 'neu').length}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Neu</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-600">
                    {inquiries.filter(i => i.status === 'bearbeitet').length}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">In Bearbeitung</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {inquiries.filter(i => i.status === 'abgeschlossen').length}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Abgeschlossen</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="font-semibold text-gray-700">Filter:</span>
              <div className="flex space-x-2">
                {['all', 'neu', 'bearbeitet', 'abgeschlossen'].map((status) => (
                  <Button
                    key={status}
                    onClick={() => setFilter(status)}
                    variant={filter === status ? 'default' : 'outline'}
                    className={filter === status ? 'bg-[#8B1538] hover:bg-[#6B0F2A]' : ''}
                  >
                    {status === 'all' ? 'Alle' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
            <Button onClick={fetchInquiries} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
          </div>

          {/* Inquiries List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B1538]"></div>
              <p className="mt-4 text-gray-600">Lade Anfragen...</p>
            </div>
          ) : inquiries.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">Keine Anfragen gefunden.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {inquiries.map((inquiry) => (
                <Card key={inquiry.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <CardTitle className="text-xl">{inquiry.company}</CardTitle>
                          <Badge className={getStatusColor(inquiry.status)}>
                            {inquiry.status.charAt(0).toUpperCase() + inquiry.status.slice(1)}
                          </Badge>
                          <Badge variant="outline">{getGewerkName(inquiry.gewerk)}</Badge>
                        </div>
                        <div className="flex items-center text-sm text-gray-500 space-x-4">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDate(inquiry.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-4 h-4 text-gray-500" />
                          <span className="font-semibold text-gray-700">Ansprechpartner:</span>
                          <span className="text-gray-600">{inquiry.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <a href={`mailto:${inquiry.email}`} className="text-[#8B1538] hover:underline">
                            {inquiry.email}
                          </a>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <a href={`tel:${inquiry.phone}`} className="text-[#8B1538] hover:underline">
                            {inquiry.phone}
                          </a>
                        </div>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-700 mb-2">Nachricht:</div>
                        <div className="bg-gray-50 p-4 rounded-lg text-gray-700 leading-relaxed">
                          {inquiry.message}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};